import { DeleteAction, EditAction } from "@/components/Action";
import StatusComponent from "@/components/Status";
import { DATE_FORMAT, AllUserRoles, UserStatus } from "@/constants/default";
import { usePermissions } from "@/rbac";
import { permissions } from "@/rbac/permissions";
import { User } from "@/types/user.type";
import { formatDateTimeAutoToString } from "@/utils/format";
import { ColumnDef } from "@tanstack/react-table";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store";

export default function TableHeader(): ColumnDef<User>[] {
    const {t} = useTranslation();
    const { hasPermission } = usePermissions();
    const navigate = useNavigate()
    const location = useLocation()
    const { auth } = useSelector((state: RootState) => state.auth)

    const baseColumns: ColumnDef<User>[] = [
        {
            header: t("table.name"),
            accessorKey: "name"
        },
        {
            header: t("table.email"),
            accessorKey: "email",
            enableSorting: false
        },
        {
            header: t("table.role"),
            accessorKey: "role",
            enableSorting: false,
            cell: (info) => _.find(AllUserRoles(), item => item.value === info.getValue())?.label
        },
        {
            header: t("table.status"),
            accessorKey: "status",
            enableSorting: false,
            cell: ({ row }) => <StatusComponent status={row.original.status} title={UserStatus(row.original.status)} />
        },
    ]

    // Discord Integration columns (Admin/SuperAdmin only)
    const discordColumns: ColumnDef<User>[] = auth.role <= 2 ? [
        {
            header: "Discord ID",
            accessorKey: "discord_user_id",
            enableSorting: false,
            cell: ({ row }) => {
                if (!row.original.discord_user_id) {
                    return <span className="text-gray-400">-</span>
                }
                return (
                    <div className="flex flex-col">
                        <span>{row.original.discord_user_id}</span>
                        {row.original.api_only && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">(API Only)</span>
                        )}
                    </div>
                )
            }
        },
    ] : []

    return [
        ...baseColumns,
        ...discordColumns,
        {
            header: t("table.created_at"),
            accessorKey: "created_at",
            cell: ({ row }) => formatDateTimeAutoToString(row.original.created_at, DATE_FORMAT)
        },
        {
            header: t("table.action"),
            accessorKey: "action",
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex items-center space-x-3.5">
                    {
                        hasPermission(permissions.USER.EDIT) && <EditAction onClick={() => navigate(location , {state: {modal: "/users/edit", id: row.original.id}})} />
                    }
                    {
                        hasPermission(permissions.USER.DELETE) && <DeleteAction onClick={() => navigate(location , {state: {modal: "/users/delete", id: row.original.id}})} />
                    }
                </div>
            )
        }
    ]
}
